import type { SearchParams } from '@seek/chalice-types';
import { useHubble } from '@seek/hubble';
import { normaliseSearchQuery } from '@seek/seek-jobs-seo';
import {
  Box,
  Button,
  Stack,
  Columns,
  Column,
  Inline,
  Divider,
} from 'braid-design-system';
import omit from 'lodash/omit';
import { useCallback, useEffect, useState } from 'react';

import { ButtonLink } from 'src/components/NavLink/NavLink';
import { SEODrawer } from 'src/components/SEODrawer/SEODrawer';
import { parseCookies } from 'src/modules/safe-cookies';
import createApiClient from 'src/modules/seek-jobs-api-client';
import { useSelector } from 'src/store/react';
import { selectFeatureFlag, selectSearchQuery } from 'src/store/selectors';

import { getRefineSearchQuery } from '../utils/utils';

import { useSecondaryFilterContext } from './context/SecondaryFilterContext';
import {
  Classification,
  ListingTime,
  RemoteOption,
  SalaryRange,
  WorkType,
} from './filters';

type SecondaryFilterSelection = Pick<
  SearchParams,
  | 'daterange'
  | 'classification'
  | 'subclassification'
  | 'salaryrange'
  | 'salarytype'
  | 'worktype'
  | 'workarrangement'
>;

export const SecondaryFilter = () => {
  const {
    country,
    locale,
    zone,
    filterSelection,
    setFilterSelection,
    isOpen,
    setIsOpen,
  } = useSecondaryFilterContext();

  const [displayTotalCount, setDisplayTotalCount] = useState(0);
  const state = useSelector((storeState) => storeState);
  const searchQuery = getRefineSearchQuery(useSelector(selectSearchQuery));
  const hubble = useHubble();
  const visitorId = hubble.visitorId();

  const {
    location: { requestId },
    user: { testHeaders, userClientId, sessionId },
  } = state;
  const api = createApiClient();
  const remoteSearchFilterFeatureToggle = useSelector(
    selectFeatureFlag('remoteSearchFilter'),
  );

  const getJobSearchCount = useCallback(
    (searchParams: SearchParams) => {
      const cookies = parseCookies();
      const normalisedSearchParams = normaliseSearchQuery({
        path: '/jobs',
        query: searchParams,
      });
      return api.jobs
        .count({
          searchParams: normalisedSearchParams,
          zone,
          cookies,
          requestId,
          sessionId,
          userClientId,
          country,
          testHeaders,
          locale,
          solId: visitorId,
        })
        .then((result: number) => {
          setDisplayTotalCount(result);
        })
        .catch((_: Error) => {
          setDisplayTotalCount(0);
        });
    },
    [
      api.jobs,
      country,
      locale,
      requestId,
      sessionId,
      testHeaders,
      userClientId,
      visitorId,
      zone,
    ],
  );

  useEffect(() => {
    if (filterSelection) {
      getJobSearchCount(filterSelection);
    }
  }, [getJobSearchCount, filterSelection]);

  return (
    <SEODrawer
      id="secondaryFilters"
      headingLevel="2"
      title="Filters"
      width="small"
      open={isOpen}
      position="right"
      onClose={() => {
        setFilterSelection(searchQuery);
        setIsOpen(false);
      }}
      footer={<SecondaryFilterFooter displayTotalCount={displayTotalCount} />}
    >
      <Stack space="gutter">
        <WorkType />
        <Divider />
        {remoteSearchFilterFeatureToggle && (
          <>
            <RemoteOption />
            <Divider />
          </>
        )}
        <SalaryRange />
        <Divider />
        <Classification />
        <Divider />
        <ListingTime />
      </Stack>
    </SEODrawer>
  );
};

const SecondaryFilterFooter = ({
  displayTotalCount,
}: {
  displayTotalCount: number;
}) => {
  const { isOpenSearch, filterSelection, setFilterSelection, setIsOpen } =
    useSecondaryFilterContext();

  return (
    <Box background="surface" padding={['small', 'large']}>
      <Columns space="none" alignY="center">
        <Column>
          <Inline space="none" align="left">
            <Button
              data={{ automation: 'secondary-filter-clear-all' }}
              tone="neutral"
              variant="transparent"
              onClick={() => {
                setFilterSelection({
                  ...omit<SearchParams, keyof SecondaryFilterSelection>(
                    filterSelection,
                    [
                      'daterange',
                      'classification',
                      'subclassification',
                      'salaryrange',
                      'salarytype',
                      'worktype',
                      'workarrangement',
                    ],
                  ),
                });
              }}
              bleed
            >
              Clear all
            </Button>
          </Inline>
        </Column>
        <Column width="content">
          <ButtonLink
            data={{ automation: 'secondary-filter-seek-button' }}
            tone="brandAccent"
            variant="solid"
            onClick={() => {
              setIsOpen(false);
            }}
            location={{
              pathname: '/jobs',
              query: filterSelection,
            }}
          >
            {isOpenSearch || displayTotalCount === 0
              ? `SEEK`
              : `SEEK ${displayTotalCount.toLocaleString()} ${
                  displayTotalCount < 2 ? 'job' : 'jobs'
                }`}
          </ButtonLink>
        </Column>
      </Columns>
    </Box>
  );
};
